import {
    Grid,
    FormControlLabel,
    makeStyles,
    Theme,
    createStyles,
    FormControl,
    MenuItem,
    Typography,
    InputAdornment,
    RadioGroup,
    Radio,
    TextField,
    useMediaQuery,
    Button,
    GridSize,
} from "@material-ui/core";
import React, { useState, ChangeEvent, useEffect } from "react";
import { ClockOutline, Autorenew, MapMarker, MapMarkerPlus, ClipboardText, Bullhorn, CardAccountDetailsOutline } from "mdi-material-ui";
import { Shift } from "../../../stores/models/Shift";
import WeekdayButtons from "./WeekdayButtons";
import { MonthlyRepetitionType } from "../../../stores/models/RepetitionPattern";
import { observer } from "mobx-react";
import { SingularFrequencies, PluralFrequencies, } from "../../../logic/RepetitionPatternLogic";
import clsx from 'clsx';
import { DaysOfTheWeek, RepetitionEndType, RepetitionFrequency } from "../../../stores/models/interfaces/IRepetitionPattern";
import { Address } from "../../../stores/models/Address";
import { Role } from "../../../stores/models/Role";
import AddressDialog from "../../Shared/AddressDialog";
import { DialogState } from "../../../stores/models/DialogState";
import { Alert, AlertTitle } from "@material-ui/lab";
import { getNewOpportunityLink } from "../../Navigation/Links";
import { useNavigateInternally } from "../../Navigation/Hooks";
import REMSelect from "../../../shared/modules/rem-conversion/components/Select";
import REMKeyboardDatePicker from "../../../shared/modules/rem-conversion/components/KeyboardDatePicker";
import REMKeyboardTimePicker from "../../../shared/modules/rem-conversion/components/KeyboardTimePicker";
import { useRootStore } from "../../../shared/general/hooks";
import { convertTimestampToCurrentTimeZone } from "../../../logic/TimeZones";
import TimeZoneSelect from "../../../shared/modules/input/select/TimeZoneSelect";
import { isValid } from "date-fns";
import { DateFormatter } from "../../../logic/DateFormatter";
import { useStandardStyles } from "../../../shared/general/hooks/useStandardStyles";
import TextFieldWithCharacterLimit from "../../Shared/TextFieldWithCharacterLimit";
import InformationTooltip from "../../Authentication/RegistrationSteps/Organization/InformationTooltip";
import { INotifiable } from "../../../stores/models/interfaces/INotifiable";
import { InheritanceModes } from "../../../stores/models/constants/InheritanceModes";
import { IOpportunity } from "../../../stores/models/Opportunity";
import { InheritanceResolver } from "../../../logic/InheritanceResolver";
import ShiftLeadsSelection from "./ShiftLeadsSelection";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        text: {
            display: 'flex',
            alignItems: 'center',
            marginRight: theme.spacing(2)
        },
        subtitle: {
            paddingTop: '5px',
            [theme.breakpoints.down('xs')]: {
                textAlign: 'left',
                fontWeight: 'bold'
            }
        },
        endingSubtitle: {
            paddingTop: '9px',
            textAlign: 'right',
            [theme.breakpoints.down('xs')]: {
                textAlign: 'left',
                fontWeight: 'bold'
            }
        },
        icon: {
            padding: theme.spacing(1),
            color: 'rgba(0, 0, 0, 0.54)'
        },
        container: {
            marginBottom: theme.spacing(1)
        },
        numberField: {
            width: '1.875rem',
            marginRight: theme.spacing(2)
        },
        radioButton: {
            marginLeft: -11
        },
        radioField: {
            paddingTop: '4px',
        },
        xOverflowHidden: {
            overflowX: 'hidden'
        },
        fullWidth: {
            maxWidth: '100%'
        },
        pointer: {
            cursor: 'pointer'
        },
        addLocationButton: {
            marginLeft: theme.spacing(1),
            marginTop: theme.spacing(1),
            textTransform: 'none'
        },
        alert: {
            marginBottom: theme.spacing(2),
            '& > .MuiAlert-message': {
                width: '100%'
            }
        },
        errorButton: {
            justifyContent: 'flex-end',
            marginTop: theme.spacing(1)
        },
        sectionHeader: {
            padding: theme.spacing(1, 3),
            margin: theme.spacing(0, 0, 1),
            '& > :first-child': {
                margin: theme.spacing(0)
            }
        },
        section: {
            padding: theme.spacing(0, 3)
        },
        infoIcon: {
            paddingTop: theme.spacing(0),
            paddingBottom: theme.spacing(0),
            marginLeft: theme.spacing(-.5),
            marginTop: theme.spacing(.5),
        },
        marginBottom: {
            marginBottom: theme.spacing(2)
        }
    })
);

type ColumnWidths = {
    labels: {
        md: GridSize;
        sm: GridSize;
        xs: GridSize;
    };
    inputs: {
        md: GridSize;
        sm: GridSize;
        xs: GridSize;
    };
}

const columnWidths: ColumnWidths = {
    labels: {
        md: 3,
        sm: 3,
        xs: 12
    },
    inputs: {
        md: 9,
        sm: 9,
        xs: 12
    }
}

interface ShiftEntryProps {
    shift: Shift;
    locationOptions: Address[];
    inlineLocationAdding?: boolean;
    opportunityError?: string;
    opportunityOptions?: Role[];
    onOpportunityChanged?: (opportunityId: number) => void;
    opportunity?: IOpportunity | { id: number };
}

interface DateTimeEntryFieldData {
    label: string;
    date: Date | null;
    time: Date | null;
    dateError: string;
    timeError: string;
    onDateChange: (newValue: Date | null) => void;
    onTimeChange: (newValue: Date | null) => void;
}

const ShiftEntry = observer((props: ShiftEntryProps) => {

    /********* React Hooks *********/

    const classes = useStyles();
    const baseClasses = useStandardStyles();
    const xsDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'));
    const navigate = useNavigateInternally();
    const rootStore = useRootStore();
    const userStore = rootStore.userStore;

    const organizationSettings = userStore.user.organization?.settings;

    /********* Helper methods *********/

    const getIndexOfAddress = (addressId: number) => {
        return props.locationOptions.findIndex(address => address.id === addressId);
    }

    const getSelectedAddress = () => {
        if (props.shift.address) {
            const index = getIndexOfAddress(props.shift.address.id);
            if (index !== -1) {
                return props.shift.address.id;
            }
        }
        return 0;
    }

    /********* State *********/

    const [initialFocusedDate] = useState(new Date());

    const repetitionPattern = props.shift.repetitionPattern;
    const ending = repetitionPattern.ending;
    const defaultEndingDate = ending.endType === RepetitionEndType.EndDate ? ending.endDate : repetitionPattern.defaultRepetitionEndDate;
    const [endingDate, setEndingDate] = useState(defaultEndingDate);

    const defaultOccurrences = ending.endType === RepetitionEndType.AfterOccurrences ? ending.numOccurrences : 2;
    const [occurrences, setOccurrences] = useState(defaultOccurrences);

    const [locationOptions, setLocationOptions] = useState(props.locationOptions);
    const [location, setLocation] = useState(getSelectedAddress());
    const [opportunityId, setOpportunityId] = useState(props.opportunity?.id);

    const [slots, setSlots] = useState(props.shift.unlimitedSlots ? '1' : props.shift.slots.toString());

    const [dialogState] = useState(new DialogState());
    const [newAddress] = useState(new Address());
    const [inheritanceResolver] = useState(new InheritanceResolver());

    const [resolvedOpportunityLeads, setResolvedOpportunityLeads] = useState<INotifiable[]>([]);

    const getOrganizationAdmins = () => {
        return props.opportunity && 'organization' in props.opportunity && props.opportunity.organization ? props.opportunity.organization.admins : [];
    }

    const [organizationAdmins, setOrganizationAdmins] = useState(getOrganizationAdmins());

    const getOpportunityLeads = () => {
        return props.opportunity && 'leads' in props.opportunity ? props.opportunity.leads : [];
    }

    const [opportunityLeads, setOpportunityLeads] = useState(getOpportunityLeads());

    const propsOpportunityLeads = props.opportunity && 'leads' in props.opportunity ? props.opportunity?.leads : undefined;
    const propsOrganizationAdmins = props.opportunity && 'organization' in props.opportunity && props.opportunity.organization ? props.opportunity.organization.admins : undefined;

    /********* Effects *********/

    useEffect(() => {
        if (props.shift.repetitionPattern.timeZone === undefined && organizationSettings) {
            props.shift.repetitionPattern.setTimeZone(organizationSettings.defaultTimeZone);
        }
    }, [])

    useEffect(() => {
        setOpportunityLeads(getOpportunityLeads());
    }, [propsOpportunityLeads]);

    useEffect(() => {
        setOrganizationAdmins(getOrganizationAdmins());
    }, [propsOrganizationAdmins]);

    useEffect(() => {
        setEndingDate(ending.endDate);
    }, [ending.endDate]);

    useEffect(() => {
        setOccurrences(ending.numOccurrences);
    }, [ending.numOccurrences]);

    useEffect(() => {
        setOpportunityId(props.opportunity?.id);
    }, [props.opportunity?.id]);

    useEffect(() => {
        const levels = [
            { mode: InheritanceModes.Replace, values: organizationAdmins },
            { mode: opportunityLeads.length > 0 ? InheritanceModes.Replace : InheritanceModes.Inherit, values: opportunityLeads },
        ]
        setResolvedOpportunityLeads(inheritanceResolver.resolve(levels));
    }, [organizationAdmins, opportunityLeads]);

    /********* Event Handlers *********/

    const onFrequencyChanged =
        (event: React.ChangeEvent<{ name?: string | undefined; value: unknown; }>, child: React.ReactNode) => {
            const newValue = event.target.value as RepetitionFrequency;
            repetitionPattern.setFrequency(newValue);
        }

    const onRepetitionIntervalChanged = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const parsedValue = parseInt(event.target.value);
        const newValue = !Number.isNaN(parsedValue) ? parsedValue : 0;
        repetitionPattern.setInterval(newValue);
    }

    const onCustomFrequencyChanged =
        (event: React.ChangeEvent<{ name?: string | undefined; value: unknown; }>, child: React.ReactNode) => {
            const descriptor = event.target.value as SingularFrequencies | PluralFrequencies;
            repetitionPattern.setCustomFrequencyDescriptor(descriptor);
        }

    const onDaysOfWeekChanged = (selectedDays: DaysOfTheWeek[]) => {
        if (selectedDays.length > 0) {
            repetitionPattern.setDaysOfTheWeek(selectedDays);
        }
    }

    const onMonthlyRepetitionPatternChanged = (event: React.ChangeEvent<{
        name?: string | undefined;
        value: unknown;
    }>, child: React.ReactNode) => {
        const newPattern = event.target.value as MonthlyRepetitionType;
        repetitionPattern.setMonthlyRepetitionPattern(newPattern);
    }

    const onEndTypeChanged = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
        const endType = value as unknown as RepetitionEndType;
        ending.setEndType(endType);
    }

    const onOccurrencesChanged = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newValue = parseInt(event.target.value);
        ending.setNumOccurrences(newValue);
    }

    const onLocationChanged = (event: React.ChangeEvent<{ name?: string | undefined; value: unknown; }>, child: React.ReactNode) => {
        const newValue = event.target.value as number;
        const addressIndex = getIndexOfAddress(newValue);
        if (addressIndex !== -1) {
            props.shift.setAddress(locationOptions[addressIndex]);
        } else {
            props.shift.setAddress();
        }
        setLocation(newValue);
    }

    const onTimeZoneChanged = (event: React.ChangeEvent<{ name?: string | undefined; value: unknown; }>, child: React.ReactNode) => {
        const newValue = event.target.value as string | number;
        const noTimeZoneSelected = typeof newValue === 'number' || !isNaN(parseInt(newValue));
        if (noTimeZoneSelected) {
            props.shift.repetitionPattern.setTimeZone(undefined);
        } else {
            props.shift.repetitionPattern.setTimeZone(newValue);
        }
    }

    const onNewAddressAdded = (address: Address) => {
        const index = locationOptions.findIndex(location => location.id === -1);
        const newAddressAlreadyAdded = index !== -1;
        let locationOptionsCopy = locationOptions.slice();
        if (newAddressAlreadyAdded) {
            locationOptionsCopy[index] = address; // Replace the address with the newest one
        } else {
            locationOptionsCopy.push(address);
        }
        setLocationOptions(locationOptionsCopy);
        setLocation(-1);
        props.shift.setAddress(address);
        dialogState.setOpen(false);
    }

    const onOpportunityChanged = (event: React.ChangeEvent<{ name?: string | undefined; value: unknown; }>, child: React.ReactNode) => {
        const newValue = event.target.value as number;
        setOpportunityId(newValue);
        if (props.onOpportunityChanged) {
            props.onOpportunityChanged(newValue);
        }
    }

    const onSlotsChanged = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const parsedValue = parseInt(event.target.value);
        const newValue = !Number.isNaN(parsedValue) ? parsedValue : 0;
        props.shift.setSlots(newValue);
        setSlots(newValue.toString());
    }

    const onSlotsRadioSelectionChanged = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
        if (value === 'unlimited') {
            props.shift.setSlots(-1);
        } else {
            onLimitedSlotsSelected();
        }
    }

    const onLimitedSlotsSelected = () => {
        props.shift.setSlots(parseInt(slots));
    }

    const linkToNewOpportunity = () => {
        navigate(getNewOpportunityLink());
    }

    /********* Helper methods *********/

    const getTimestampInCurrentTimeZone = (date: Date, time: Date, timeZone: string) => {
        if (isValid(date) && isValid(time)) {
            const timestamp = DateFormatter.getTimestampFromDateAndTime(date, time);
            return convertTimestampToCurrentTimeZone(timestamp, timeZone);
        } else {
            return null;
        }
    }

    const onDateOrTimeFieldChanged = (
        fieldChanged: 'date' | 'time',
        date: Date | null,
        time: Date | null,
        timezone: string | undefined,
        fieldSetter: (date: Date | null) => void,
        timestampSetter: (date: Date | null) => void
    ) => {
        if (date === null || !isValid(date) || time === null || !isValid(time) || timezone === undefined) {
            // Case 1: Either (or both) the date and time are invalid
            fieldSetter(fieldChanged === 'date' ? date : time);
        } else {
            // Case 2: Both the date and time are valid
            const newTimestamp = getTimestampInCurrentTimeZone(date, time, timezone);
            if (newTimestamp) {
                timestampSetter(newTimestamp);
            }
        }
    }

    /********* Helper constants *********/

    const dateTimeEntryFieldData: DateTimeEntryFieldData[] = [{
        label: "Starting",
        date: repetitionPattern.startDateInTimeZone,
        time: repetitionPattern.startTimeInTimeZone,
        dateError: repetitionPattern.errors.startDate,
        timeError: repetitionPattern.errors.startTime,
        onDateChange: (date: Date | null) => {
            onDateOrTimeFieldChanged(
                'date',
                date,
                repetitionPattern.startTimeInTimeZone,
                repetitionPattern.timeZone,
                (date: Date | null) => repetitionPattern.setStartDate(date),
                (date: Date | null) => repetitionPattern.setStartTimestamp(date)
            );
        },
        onTimeChange: (time: Date | null) => {
            onDateOrTimeFieldChanged(
                'time',
                repetitionPattern.startDateInTimeZone,
                time,
                repetitionPattern.timeZone,
                (time: Date | null) => repetitionPattern.setStartTime(time),
                (time: Date | null) => repetitionPattern.setStartTimestamp(time)
            );
        }
    }, {
        label: "Ending",
        date: repetitionPattern.endDateInTimeZone,
        time: repetitionPattern.endTimeInTimeZone,
        dateError: repetitionPattern.errors.endDate,
        timeError: repetitionPattern.errors.endTime,
        onDateChange: (date: Date | null) => {
            onDateOrTimeFieldChanged(
                'date',
                date,
                repetitionPattern.endTimeInTimeZone,
                repetitionPattern.timeZone,
                (date: Date | null) => repetitionPattern.setEndDate(date),
                (time: Date | null) => repetitionPattern.setEndTimestamp(time),
            );
        },
        onTimeChange: (time: Date | null) => {
            onDateOrTimeFieldChanged(
                'time',
                repetitionPattern.endDateInTimeZone,
                time,
                repetitionPattern.timeZone,
                (time: Date | null) => repetitionPattern.setEndTime(time),
                (time: Date | null) => repetitionPattern.setEndTimestamp(time),
            );
        }
    }];

    // Dropdown Options
    const dropdownOptions = repetitionPattern.frequencyDropdownOptions;
    const monthlyDropdownOptions = repetitionPattern.monthlyDropdownOptions;

    const requiredAsterisk = (
        <span className={baseClasses.required}> *</span>
    );

    const fieldLabel = (label: string, required?: boolean) => (
        <Grid item xs={columnWidths.labels.xs} sm={columnWidths.labels.sm} md={columnWidths.labels.md}>
            <Typography className={clsx(classes.subtitle, baseClasses.bold)} variant={xsDown ? "body1" : "subtitle2"}>{label} {required === true ? requiredAsterisk : null}</Typography>
        </Grid>
    )

    /********* Render *********/

    return (
        <div>
            <div className={clsx(classes.sectionHeader, baseClasses.lightBackground)}>
                <h4>Public Facing Information</h4>
            </div>
            {/******* Opportunity selection *******/}
            {opportunityId && props.opportunityOptions &&
                <div className={classes.section}>
                    {props.opportunityOptions.length === 0 &&
                        < Alert severity="error" className={classes.alert}>
                            <AlertTitle className={baseClasses.bold}>Opportunity Required</AlertTitle>
                            <Typography>Create a volunteer opportunity in order to schedule shifts.</Typography>
                            <div className={clsx(classes.errorButton, baseClasses.fullWidth, baseClasses.flex)}>
                                <Button variant='contained' color='primary' onClick={() => linkToNewOpportunity()}>Create an opportunity</Button>
                            </div>
                        </Alert>
                    }
                </div>
            }
            {props.opportunityOptions &&
                <div className={classes.section}>
                    <Grid container spacing={2} className={classes.container}>
                        {fieldLabel('Opportunity', true)}
                        <Grid item xs={columnWidths.inputs.xs} sm={columnWidths.inputs.sm} md={columnWidths.inputs.md} className={classes.xOverflowHidden}>
                            <FormControl variant="standard" className={classes.fullWidth}>
                                <REMSelect
                                    id="opportunity-dropdown"
                                    value={opportunityId || -1}
                                    onChange={onOpportunityChanged}
                                    startAdornment={
                                        <InputAdornment position="start" className={classes.icon}>
                                            <ClipboardText />
                                        </InputAdornment>
                                    }
                                >
                                    <MenuItem value={-1}>
                                        Select an Opportunity
                                    </MenuItem>
                                    {props.opportunityOptions.map((role, index) => {
                                        return (
                                            <MenuItem key={`role-${index}`} value={role.id}>
                                                {role.position}
                                            </MenuItem>
                                        );
                                    })}
                                </REMSelect>
                                {props.opportunityError &&
                                    <Typography color='error' variant='caption'>
                                        {props.opportunityError}
                                    </Typography>
                                }
                            </FormControl>
                        </Grid>
                    </Grid>
                </div>
            }

            <div className={classes.section}>
                <Grid container spacing={2} className={classes.container}>
                    <Grid item xs={columnWidths.labels.xs} sm={columnWidths.labels.sm} md={columnWidths.labels.md}>
                        <div className={clsx(baseClasses.flex, baseClasses.alignCenter)}>
                            <Typography className={clsx(classes.subtitle, baseClasses.bold)} variant={xsDown ? "body1" : "subtitle2"}>Role</Typography>
                            <InformationTooltip
                                title='Optional. Useful for labeling shifts that cover a specific set of volunteer needs related to the overall opportunity. For example, an opportunity post for an event might include shifts like “Setup Crew” or “Registration/Check-in.”'
                                IconButtonProps={{ className: classes.infoIcon }}
                            />
                        </div>
                    </Grid>
                    <Grid item xs={columnWidths.inputs.xs} sm={columnWidths.inputs.sm} md={columnWidths.inputs.md} className={classes.xOverflowHidden}>
                        <FormControl variant="standard" className={baseClasses.fullWidth}>
                            <TextFieldWithCharacterLimit
                                characterLimit={150}
                                TextFieldProps={{
                                    id: "shift-label-input",
                                    // label: "Notes",
                                    // variant: "outlined",
                                    // required: true,
                                    // disabled: isSubmitting || values.fieldProperties?.mission?.disabled,

                                    value: props.shift.label || "",

                                    InputProps: {
                                        startAdornment: (
                                            <InputAdornment position="start" className={classes.icon}>
                                                <CardAccountDetailsOutline />
                                            </InputAdornment>
                                        ),
                                    },
                                    // startIcon: Tag,
                                    placeholder: "Clean-up Crew",
                                    onChange: (event) => props.shift.setLabel(event.target.value),
                                    className: baseClasses.fullWidth,
                                    // error: missionError.length > 0,
                                    // helperText:
                                    //     missionError.length > 0
                                    //         ? missionError
                                    //         : "In a sentence or two, explain your organization's aim."
                                }}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </div>

            {/******* Starting and ending dates and times *******/}
            <div className={classes.section}>
                {
                    dateTimeEntryFieldData.map((data, index) => {
                        return (
                            <Grid container spacing={2} className={classes.container} key={`date-time-entry-field-${index}`}>
                                {/* Label */}
                                {fieldLabel(data.label, true)}
                                {/* Date field */}
                                <Grid item xs={columnWidths.inputs.xs} sm={columnWidths.inputs.sm} md={columnWidths.inputs.md}>
                                    <Grid container spacing={2}>
                                        <Grid item md={6}>
                                            <REMKeyboardDatePicker
                                                id={`date-picker-${index}`}
                                                format="MM/dd/yy"
                                                // minDate={new Date()}
                                                value={data.date}
                                                inputVariant="standard"
                                                disableToolbar
                                                variant="inline"
                                                required
                                                KeyboardButtonProps={{
                                                    'aria-label': 'change date',
                                                }}
                                                InputAdornmentProps={{ position: "start" }}
                                                autoOk
                                                initialFocusedDate={initialFocusedDate}
                                                onChange={(date, value) => { data.onDateChange(date); }}
                                                helperText={data.dateError.length > 0 ? data.dateError : ""}
                                                error={data.dateError.length > 0}
                                            />
                                        </Grid>
                                        {/* Time field */}
                                        <Grid item md={6}>
                                            <REMKeyboardTimePicker
                                                id={`time-picker-${index}`}
                                                format="h:mm a"
                                                mask="__:__ _M"
                                                minutesStep={5}
                                                value={data.time}
                                                variant="inline"
                                                inputVariant="standard"
                                                required
                                                KeyboardButtonProps={{
                                                    'aria-label': 'change time',
                                                }}
                                                InputAdornmentProps={{ position: "start" }}
                                                keyboardIcon={<ClockOutline />}
                                                onChange={(time, value) => { data.onTimeChange(time); }}
                                                error={data.timeError.length > 0}
                                                helperText={data.timeError.length > 0 ? data.timeError : ""}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        );
                    })
                }

                {/******* Timezone *******/}
                <Grid container spacing={2} className={classes.container}>
                    {fieldLabel('Time Zone', true)}
                    <Grid item xs={columnWidths.inputs.xs} sm={columnWidths.inputs.sm} md={columnWidths.inputs.md} className={classes.xOverflowHidden}>
                        <FormControl variant="standard" className={classes.fullWidth}>
                            <TimeZoneSelect
                                value={props.shift.repetitionPattern.timeZone || 0}
                                onChange={onTimeZoneChanged}
                            />
                            {repetitionPattern.errors.timeZone.length > 0
                                ? <Typography color='error' variant='caption'>
                                    {repetitionPattern.errors.timeZone}
                                </Typography>
                                : null
                            }
                        </FormControl>
                    </Grid>
                </Grid>

                {/******* Repeats (Repetition pattern selection) *******/}
                {
                    !props.shift.isShiftInstance
                        ? <Grid container spacing={2} className={classes.container}>
                            {fieldLabel('Repeats', true)}
                            <Grid item xs={columnWidths.inputs.xs} sm={columnWidths.inputs.sm} md={columnWidths.inputs.md} className={classes.xOverflowHidden}>
                                <FormControl variant="standard" className={classes.fullWidth}>
                                    <REMSelect
                                        id="repetition-pattern-dropdown"
                                        value={repetitionPattern.frequency}
                                        onChange={onFrequencyChanged}
                                        startAdornment={
                                            <InputAdornment position="start" className={classes.icon}>
                                                <Autorenew />
                                            </InputAdornment>
                                        }
                                    >
                                        {Object.values(RepetitionFrequency).map((frequency, index) => {
                                            return (
                                                <MenuItem key={`menu-item-${index}`} value={frequency}>
                                                    {dropdownOptions[frequency]}
                                                </MenuItem>
                                            );
                                        })}
                                    </REMSelect>
                                </FormControl>
                            </Grid>
                        </Grid>
                        : null
                }

                {/* Repetition Pattern Details */}
                {
                    !repetitionPattern.repeats
                        ? null
                        : <div>

                            {/******* Frequency *******/}
                            <Grid container spacing={2} className={classes.container}>
                                {fieldLabel('Frequency', true)}
                                <Grid item xs={columnWidths.inputs.xs} sm={columnWidths.inputs.sm} md={columnWidths.inputs.md}>
                                    <div className={baseClasses.flex}>
                                        <Typography className={classes.text}>Every</Typography>
                                        <TextField
                                            value={repetitionPattern.interval}
                                            className={classes.numberField}
                                            inputProps={{ style: { textAlign: 'center' } }}
                                            onChange={onRepetitionIntervalChanged}
                                            error={repetitionPattern.errors.interval.length > 0}
                                        />
                                        {repetitionPattern.isCustomPattern
                                            ? <REMSelect value={repetitionPattern.frequencyDescriptor} onChange={onCustomFrequencyChanged}>
                                                {repetitionPattern.customFrequencyDescriptors.map((frequency, index) => {
                                                    return (
                                                        <MenuItem key={`menu-item-${index}`} value={frequency}>
                                                            {frequency}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </REMSelect>
                                            : <Typography className={classes.text}>{repetitionPattern.frequencyDescriptor}</Typography>
                                        }
                                    </div>
                                    {repetitionPattern.errors.interval.length > 0
                                        ? <Typography color='error' variant='caption'>
                                            {repetitionPattern.errors.interval}
                                        </Typography>
                                        : null}
                                </Grid>
                            </Grid>

                            {/******* Repeats on *******/}

                            {/* Days of the week selection */}
                            {repetitionPattern.isWeeklyPattern
                                ? <Grid container spacing={2} className={classes.container}>
                                    {fieldLabel('Repeats on', true)}
                                    <Grid item xs={columnWidths.inputs.xs} sm={columnWidths.inputs.sm} md={columnWidths.inputs.md}>
                                        <WeekdayButtons
                                            selectedDays={repetitionPattern.daysOfTheWeek}
                                            onSelectedDaysChanged={onDaysOfWeekChanged}
                                        />
                                    </Grid>
                                </Grid>
                                : null
                            }

                            {/* Monthly repetition type selection */}
                            {repetitionPattern.isCustomMonthlyPattern
                                ? <Grid container spacing={2} className={classes.container}>
                                    {fieldLabel('Repeats on', true)}
                                    <Grid item xs={columnWidths.inputs.xs} sm={columnWidths.inputs.sm} md={columnWidths.inputs.md} className={classes.xOverflowHidden}>
                                        <FormControl variant="standard" className={classes.fullWidth}>
                                            <REMSelect value={repetitionPattern.monthlyRepetitionPattern} onChange={onMonthlyRepetitionPatternChanged}>
                                                {Object.keys(monthlyDropdownOptions).map((key: string, index: number) => {
                                                    return (
                                                        <MenuItem key={`menu-item-${index}`} value={key}>
                                                            {monthlyDropdownOptions[key as MonthlyRepetitionType]}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </REMSelect>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                                : null
                            }

                            {/******* Ending *******/}
                            <Grid container spacing={2} className={classes.container}>
                                {fieldLabel('Ending', true)}
                                <Grid item xs={columnWidths.inputs.xs} sm={columnWidths.inputs.sm} md={columnWidths.inputs.md} className={classes.xOverflowHidden}>
                                    <FormControl component="fieldset">
                                        <RadioGroup aria-label="ending" value={ending.endType} onChange={onEndTypeChanged} >
                                            {/* Never Option */}
                                            <FormControlLabel value={RepetitionEndType.Never} control={<Radio color="primary" />} label="Never" />
                                            {/* On Date Option */}
                                            <div>
                                                <FormControlLabel
                                                    value={RepetitionEndType.EndDate}
                                                    control={<Radio color="primary" />}
                                                    label="On"
                                                />
                                                <REMKeyboardDatePicker
                                                    id="birthdate-picker"
                                                    format="MM/dd/yy"
                                                    // minDate={new Date()}
                                                    value={endingDate}
                                                    onChange={(date, value) => { ending.setEndDate(date); }}
                                                    inputVariant="standard"
                                                    variant="inline"
                                                    required
                                                    KeyboardButtonProps={{
                                                        'aria-label': 'change date',
                                                    }}
                                                    className={classes.radioField}
                                                    style={{ width: '8.125rem' }}
                                                    disabled={ending.endType !== RepetitionEndType.EndDate}
                                                    disableToolbar
                                                    error={ending.errors.endDate.length > 0}
                                                    helperText={ending.errors.endDate.length > 0 ? ending.errors.endDate : undefined}
                                                    onClick={() => ending.setEndType(RepetitionEndType.EndDate)}
                                                />
                                            </div>
                                            {/* After Number of Occurrences Option */}
                                            <div className={baseClasses.flex}>
                                                <FormControlLabel value={RepetitionEndType.AfterOccurrences} control={<Radio color="primary" />} label="After" />
                                                <TextField
                                                    inputProps={{ style: { textAlign: 'center' } }}
                                                    className={clsx(classes.numberField, classes.radioField)}
                                                    disabled={ending.endType !== RepetitionEndType.AfterOccurrences}
                                                    value={occurrences}
                                                    onChange={onOccurrencesChanged}
                                                    error={ending.errors.numOccurrences.length > 0}
                                                    onClick={() => ending.setEndType(RepetitionEndType.AfterOccurrences)}
                                                />
                                                <Typography
                                                    className={clsx(classes.text, classes.pointer)}
                                                    color={ending.endType !== RepetitionEndType.AfterOccurrences
                                                        ? 'textSecondary'
                                                        : 'inherit'}
                                                    onClick={() => ending.setEndType(RepetitionEndType.AfterOccurrences)}
                                                >
                                                    occurrences
                                                </Typography>
                                            </div>
                                            {ending.errors.numOccurrences.length > 0
                                                ? <Typography color='error' variant='caption'>
                                                    {ending.errors.numOccurrences}
                                                </Typography>
                                                : null}
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </div>
                }

                {/******* Slots *******/}
                < Grid container spacing={2} className={classes.container}>
                    {fieldLabel('Slots', true)}
                    <Grid item xs={columnWidths.inputs.xs} sm={columnWidths.inputs.sm} md={columnWidths.inputs.md} className={classes.xOverflowHidden}>
                        <FormControl component="fieldset">
                            <RadioGroup aria-label="slots" value={props.shift.unlimitedSlots ? 'unlimited' : 'limited'} onChange={onSlotsRadioSelectionChanged} >
                                {/* Limited Slots Option */}
                                <div className={baseClasses.flex}>
                                    <Radio color="primary" value={'limited'} className={classes.radioButton} />
                                    <TextField
                                        inputProps={{ style: { textAlign: 'center' } }}
                                        className={clsx(classes.numberField, classes.radioField)}
                                        disabled={props.shift.unlimitedSlots}
                                        value={slots}
                                        onChange={onSlotsChanged}
                                        error={props.shift.errors.slots.length > 0}
                                        onClick={onLimitedSlotsSelected}
                                    />
                                    <Typography
                                        className={clsx(classes.text, classes.pointer)}
                                        color={props.shift.unlimitedSlots
                                            ? 'textSecondary'
                                            : 'inherit'}
                                        onClick={onLimitedSlotsSelected}
                                    >
                                        slots
                                    </Typography>
                                </div>
                                {props.shift.errors.slots.length > 0
                                    ? <Typography color='error' variant='caption'>
                                        {props.shift.errors.slots}
                                    </Typography>
                                    : null}
                                {/* Unlimited Option */}
                                <FormControlLabel value={'unlimited'} control={<Radio color="primary" />} label="Unlimited" />
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                </Grid>

                {/******* Location selection *******/}
                <Grid container spacing={2} className={classes.container}>
                    {fieldLabel('Location', false)}
                    <Grid item xs={columnWidths.inputs.xs} sm={columnWidths.inputs.sm} md={columnWidths.inputs.md} className={classes.xOverflowHidden}>
                        {locationOptions.length > 0
                            ? <FormControl variant="standard" className={classes.fullWidth}>
                                <REMSelect
                                    id="location-dropdown"
                                    value={location}
                                    onChange={onLocationChanged}
                                    startAdornment={
                                        <InputAdornment position="start" className={classes.icon}>
                                            <MapMarker />
                                        </InputAdornment>
                                    }
                                >
                                    <MenuItem value={0}>
                                        Select a Location
                                    </MenuItem>
                                    {locationOptions.map((address, index) => {
                                        return (
                                            <MenuItem key={`address-${index}`} value={address.id}>
                                                {address.addressString}
                                            </MenuItem>
                                        );
                                    })}
                                </REMSelect>
                            </FormControl>
                            : <Typography>To link this shift to an address, you must first add or select the address from the Locations section.</Typography>
                        }
                        {props.inlineLocationAdding &&
                            <div>
                                <Button
                                    color={'primary'}
                                    variant={'text'}
                                    startIcon={<MapMarkerPlus />}
                                    className={classes.addLocationButton}
                                    onClick={() => dialogState.setOpen(true)}
                                >
                                    Add Location
                                </Button>
                                <AddressDialog
                                    state={dialogState}
                                    title={"Add Address"}
                                    address={newAddress}
                                    confirmText={"Add"}
                                    confirmIcon={MapMarkerPlus}
                                    displayRemoveButton={false}
                                    onConfirm={onNewAddressAdded}
                                />
                            </div>
                        }
                    </Grid>
                    {fieldLabel('Notes', false)}
                    <Grid item xs={columnWidths.inputs.xs} sm={columnWidths.inputs.sm} md={columnWidths.inputs.md} className={classes.xOverflowHidden}>
                        <div className={baseClasses.flex}>
                            <TextFieldWithCharacterLimit
                                characterLimit={500}
                                TextFieldProps={{
                                    id: "shift-notes-input",
                                    // label: "Notes",
                                    // variant: "outlined",
                                    // required: true,
                                    placeholder: "Consider bringing water and sunscreen.",
                                    // disabled: isSubmitting || values.fieldProperties?.mission?.disabled,
                                    value: props.shift.notes || "",
                                    onChange: (event) => props.shift.setNotes(event.target.value),
                                    // onChange: handleMissionChange,
                                    // autoFocus: true,
                                    multiline: true,
                                    rows: 1,
                                    rowsMax: 5,
                                    className: baseClasses.fullWidth,
                                    InputProps: {
                                        startAdornment: (
                                            <InputAdornment position="start" className={classes.icon}>
                                                <Bullhorn />
                                            </InputAdornment>
                                        ),
                                    },
                                    // error: missionError.length > 0,
                                    // helperText:
                                    //     missionError.length > 0
                                    //         ? missionError
                                    //         : "In a sentence or two, explain your organization's aim."
                                }}
                            />
                        </div>
                    </Grid>
                </Grid>
            </div>
            <div className={clsx(classes.sectionHeader, baseClasses.lightBackground)}>
                <h4>Internal Information</h4>
            </div>
            <div className={classes.section}>
                {/******* Shift Leads *******/}
                <Grid container spacing={2} className={classes.container}>
                    <Grid item xs={columnWidths.labels.xs} sm={columnWidths.labels.sm} md={columnWidths.labels.md}>
                        <div className={clsx(baseClasses.flex, baseClasses.alignCenter)}>
                            <Typography className={clsx(classes.subtitle, baseClasses.bold)} variant={xsDown ? "body1" : "subtitle2"}>Shift Leads</Typography>
                            <InformationTooltip
                                title="Shift leads receive admin notifications related to the shift. Emails of shift leads are not shared with volunteers."
                                IconButtonProps={{ className: classes.infoIcon }}
                            />
                        </div>
                    </Grid>
                    <Grid item xs={columnWidths.inputs.xs} sm={columnWidths.inputs.sm} md={columnWidths.inputs.md} className={classes.xOverflowHidden}>
                        <div className={baseClasses.flex}>
                            <FormControl component="fieldset" className={baseClasses.flexGrow}>
                                <ShiftLeadsSelection
                                    opportunityLeads={resolvedOpportunityLeads}
                                    organizationAdmins={organizationAdmins}
                                    shift={props.shift}
                                />
                            </FormControl>
                        </div>
                    </Grid>
                </Grid >
            </div >
        </div >
    )
});

export default ShiftEntry;